'use client';

import React from 'react';
import { Schema } from '@benzinga/seo';
import { appEnvironment, appName, truncate } from '@benzinga/utils';
import { Breadcrumb as BreadcrumbInterface } from '@benzinga/content-manager';
import classNames from 'classnames';
import { EdgeBadge } from '../Badge';

export interface BreadcrumbsProps {
  className?: string;
  data: BreadcrumbInterface[];
  enableTruncation?: boolean;
  separator?: string;
  showEdgeBadge?: boolean;
}

interface BreadcrumbProps {
  href?: string;
  index: number;
  isLast: boolean;
  isMoneyApp: boolean;
  name: string;
}

interface ItemListElement {
  '@type': string;
  item: {
    '@id': string;
    '@type': string;
    name: string;
    url: string;
  };
}

const prepareSchema = (breadcrumbs: BreadcrumbInterface[]) => {
  const BreadCrumbsItems: ItemListElement[] = [];

  breadcrumbs.forEach((bc: BreadcrumbInterface, i: number) => {
    const item = {
      '@type': 'ListItem',
      item: {
        '@id': 'https://www.benzinga.com' + bc.href,
        '@type': 'WebPage',
        name: bc.name,
        url: 'https://www.benzinga.com' + bc.href,
      },
      position: i + 1,
    };
    BreadCrumbsItems[i] = item;
  });

  const schemaObj = {
    '@context': 'https://schema.org/',
    '@type': 'BreadcrumbList',
    itemListElement: BreadCrumbsItems,
  };
  return schemaObj;
};

const separatorClasses =
  'breadcrumbs-separator text-gray-400 select-none pointer-events-none text-sm font-light mx-0.5 shrink-0 aria-hidden';

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  className,
  data,
  enableTruncation,
  separator = '›',
  showEdgeBadge = false,
}) => {
  if (!Array.isArray(data) || data.length <= 1) return null;
  const isMoneyApp = appEnvironment().isApp(appName.money);

  return (
    <nav
      aria-label="Breadcrumb"
      className={classNames('breadcrumbs flex flex-wrap items-center min-h-8 gap-x-0 text-sm text-gray-600', className)}
      role="navigation"
    >
      {data && <Schema data={prepareSchema(data)} name="breadcrumb" />}
      <ol className="breadcrumbs-list flex flex-wrap items-center gap-x-0 list-none p-0 m-0">
        {data.map(({ href, name }, index) => {
          const isLast = data.length - 1 === index;
          if (!name) return null;
          const truncatedName = enableTruncation ? truncate(name, 70) : name;
          return (
            <li className="breadcrumbs-item flex items-center gap-x-0 shrink-0" key={name}>
              <Breadcrumb href={href} index={index} isLast={isLast} isMoneyApp={isMoneyApp} name={truncatedName} />
              {!isLast && (
                <span aria-hidden className={separatorClasses}>
                  {separator}
                </span>
              )}
            </li>
          );
        })}
      </ol>
      {showEdgeBadge && <div className="ml-2 mt-0.5 scale-125">{EdgeBadge}</div>}
    </nav>
  );
};

const Breadcrumb: React.FC<BreadcrumbProps> = ({ href, index, isLast, isMoneyApp, name }) => {
  const base =
    'transition-colors duration-150 outline-none rounded focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1';
  const itemClasses = classNames(
    base,
    'text-sm',
    isLast && 'breadcrumbs-current',
    isMoneyApp ? 'text-white/90' : 'text-gray-600',
    isLast && (isMoneyApp ? 'text-white font-medium' : 'text-gray-900 font-medium'),
    !isLast &&
      href &&
      (isMoneyApp
        ? 'hover:text-white underline-offset-2 hover:underline'
        : 'hover:text-blue-600 underline-offset-2 hover:underline'),
  );

  if (isLast) {
    return (
      <span aria-current="page" className={itemClasses} key={index}>
        {name}
      </span>
    );
  }

  if (href) {
    return (
      <a
        aria-label={`Navigate to ${name}`}
        className={classNames(itemClasses, 'breadcrumbs-link')}
        href={href}
        key={index}
      >
        {name}
      </a>
    );
  }

  return (
    <span className={itemClasses} key={index}>
      {name}
    </span>
  );
};

export default Breadcrumbs;
